/**
 * AdLevelsTrendWidget — «Реклама по уровням», вьюха «Динамика»
 * ===============================================================
 *
 * Динамика одного среза по периодам регистрации: выбранный уровень кампании
 * (показы на уровне N на игрока, дошедшего до N) или выбранный день жизни
 * (показы в день D на игрока, живого в D). Определения и когорта — как у
 * таблицы: см. server/src/services/ads/adLevelsTrendService.ts.
 *
 * Гео и горизонт дня жизни делят localStorage с таблицей: переключение вьюхи
 * не сбрасывает срез. Шаг, срез и значение — свои.
 */

import React from 'react';
import { adsLevels as adsLevelsSpecs } from '@ggstat/chart-specs';
import { Bar, CartesianGrid, ComposedChart, Legend, Line, XAxis, YAxis } from 'recharts';
import { BaseWidget, useBaseWidget } from '../BaseWidget';
import { useAdsWidgetData } from '../../../hooks/useAdsGeo';
import { useLocalStorage } from '../../../hooks/useLocalStorage';
import { AdsService } from '../../../services/adsService';
import { Label } from '../../ui/label';
import { ToggleGroup, ToggleGroupItem } from '../../ui/toggle-group';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Tooltip } from '../../charts/OrderedTooltip';
import { ResponsiveChart as ResponsiveContainer } from '../../ui/responsive-chart';
import { CommitInput, parseBounded } from './adLevels/CommitInput';
import {
  GROUP_BY_OPTIONS,
  MODE_DAY,
  MODE_LEVEL,
  VALUE_PER_PLAYER,
  VALUE_SHARE,
  buildTrendRows,
  formatTrendValue,
  groupColor,
  groupSeriesKey,
  hasTrendData,
  pctFormat,
  playersLabel,
  ratioFormat
} from './adLevels/trendModel';

const LEVEL_LIMIT = 1000;
const DAY_LIMIT = 3650;
const CHART_HEIGHT = 300;
const MAIN_SERIES = adsLevelsSpecs.trend.main.series;
const PLAYERS_COLOR = MAIN_SERIES.find((s) => s.key === 'players').color;

const intFormat = new Intl.NumberFormat('ru-RU');

const AD_LINES = MAIN_SERIES.filter((s) => s.mark === 'line').map((s) => ({
  key: s.key,
  name: s.label,
  color: s.color,
  width: s.width
}));

function Field({ label, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

/** Подсказка главного графика: серии в порядке значений + контекст периода. */
function MainTooltipContent({ active, payload, playersName }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  const lines = payload.filter((item) => item.dataKey !== 'players');
  return (
    <div className="rounded-md border bg-background p-2 text-xs shadow-sm space-y-0.5">
      <div className="font-medium">
        Регистрации с {row.period}
        {row.partial ? ' (период не целиком в окне)' : ''}
      </div>
      {lines.map((item) => (
        <div key={item.dataKey} className="flex justify-between gap-4" style={{ color: item.color }}>
          <span>{item.name}</span>
          <span>{ratioFormat(item.value)}</span>
        </div>
      ))}
      <div className="flex justify-between gap-4 text-muted-foreground">
        <span>{playersName}</span>
        <span>
          {intFormat.format(row.players)} из {intFormat.format(row.cohort)} ({pctFormat(row.playersShare)})
        </span>
      </div>
      <div className="flex justify-between gap-4 text-muted-foreground">
        <span>Смотрели рекламу</span>
        <span>{pctFormat(row.watchedShare)}</span>
      </div>
      <div className="flex justify-between gap-4 text-muted-foreground">
        <span>Показов на регистрацию</span>
        <span>{ratioFormat(row.adsPerReg)}</span>
      </div>
    </div>
  );
}

function PeriodsTable({ rows, total, playersName }) {
  const head = ['Период', 'Когорта', playersName, '% когорты', 'Реклам / игрок', 'Смотрели', 'На рег.'];
  const cells = (r) => [
    intFormat.format(r.cohort),
    intFormat.format(r.players),
    pctFormat(r.playersShare),
    ratioFormat(r.adsPerPlayer),
    pctFormat(r.watchedShare),
    ratioFormat(r.adsPerReg)
  ];
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {head.map((h, i) => (
            <TableHead key={h} className={i > 0 ? 'text-right' : ''}>
              {h}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {total && (
          <TableRow className="font-medium bg-muted/40">
            <TableCell>Всё окно</TableCell>
            {cells(total).map((v, i) => (
              <TableCell key={i} className="text-right">
                {v}
              </TableCell>
            ))}
          </TableRow>
        )}
        {rows.map((r) => (
          <TableRow key={r.period} className={r.partial ? 'text-muted-foreground' : ''}>
            <TableCell>{r.label}</TableCell>
            {cells(r).map((v, i) => (
              <TableCell key={i} className="text-right">
                {v}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export function AdLevelsTrendWidget() {
  const [maxDay, setMaxDay] = useLocalStorage('ads-levels:max-day', null);
  const [mode, setMode] = useLocalStorage('ads-levels:trend-mode', MODE_LEVEL);
  const [level, setLevel] = useLocalStorage('ads-levels:trend-level', 10);
  const [day, setDay] = useLocalStorage('ads-levels:trend-day', 1);
  const [groupBy, setGroupBy] = useLocalStorage('ads-levels:trend-group-by', 'week');
  const [valueMode, setValueMode] = useLocalStorage('ads-levels:trend-value', VALUE_PER_PLAYER);

  const isDay = mode === MODE_DAY;
  const levelParam = parseBounded(level, 1, LEVEL_LIMIT) ?? 1;
  const dayParam = parseBounded(day, 0, DAY_LIMIT) ?? 0;
  const maxDayParam = parseBounded(maxDay, 0, DAY_LIMIT);
  const step = GROUP_BY_OPTIONS.some((o) => o.value === groupBy) ? groupBy : 'week';
  const target = isDay ? dayParam : levelParam;

  const trendData = useAdsWidgetData(
    async (filters, options) =>
      AdsService.get(
        '/levels/trend',
        filters,
        {
          mode: isDay ? MODE_DAY : MODE_LEVEL,
          level: isDay ? undefined : levelParam,
          day: isDay ? dayParam : undefined,
          maxDay: isDay ? undefined : (maxDayParam ?? undefined),
          groupBy: step
        },
        options
      ),
    [isDay, levelParam, dayParam, maxDayParam, step]
  );
  const widgetProps = useBaseWidget(trendData);
  const { data } = widgetProps;

  const groups = data?.groups || [];
  const rows = buildTrendRows(data?.points, groups, step, valueMode);
  const [totalRow] = data?.total ? buildTrendRows([data.total], [], step) : [];
  const playersName = playersLabel(isDay ? MODE_DAY : MODE_LEVEL, target);
  const showShare = valueMode === VALUE_SHARE;

  const controls = (
    <div className="flex flex-wrap items-end gap-4">
      <Field label="Срез">
        <ToggleGroup type="single" size="sm" value={isDay ? MODE_DAY : MODE_LEVEL} onValueChange={(v) => v && setMode(v)}>
          <ToggleGroupItem value={MODE_LEVEL} className="text-xs px-3">
            Уровень
          </ToggleGroupItem>
          <ToggleGroupItem value={MODE_DAY} className="text-xs px-3">
            День жизни
          </ToggleGroupItem>
        </ToggleGroup>
      </Field>

      {isDay ? (
        <Field label="День жизни (D0 — регистрация)">
          <CommitInput className="w-24 h-8" value={dayParam} min={0} max={DAY_LIMIT} fallback={0} onCommit={(v) => setDay(v ?? 0)} />
        </Field>
      ) : (
        <>
          <Field label="Уровень кампании">
            <CommitInput
              className="w-24 h-8"
              value={levelParam}
              min={1}
              max={LEVEL_LIMIT}
              fallback={1}
              onCommit={(v) => setLevel(v ?? 1)}
            />
          </Field>
          <Field label="Макс. день жизни">
            <CommitInput
              className="w-24 h-8"
              value={maxDayParam}
              min={0}
              max={DAY_LIMIT}
              placeholder="все"
              onCommit={(v) => setMaxDay(v)}
            />
          </Field>
        </>
      )}

      <Field label="Шаг регистраций">
        <ToggleGroup type="single" size="sm" value={step} onValueChange={(v) => v && setGroupBy(v)}>
          {GROUP_BY_OPTIONS.map((o) => (
            <ToggleGroupItem key={o.value} value={o.value} className="text-xs px-3">
              {o.label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </Field>

      <Field label="Плейсменты">
        <ToggleGroup type="single" size="sm" value={valueMode} onValueChange={(v) => v && setValueMode(v)}>
          <ToggleGroupItem value={VALUE_PER_PLAYER} className="text-xs px-3">
            На игрока
          </ToggleGroupItem>
          <ToggleGroupItem value={VALUE_SHARE} className="text-xs px-3">
            % игроков
          </ToggleGroupItem>
        </ToggleGroup>
      </Field>
    </div>
  );

  const sliceText = isDay
    ? `показы в D${dayParam} на игрока, живого в этот день`
    : `показы на уровне ${levelParam} на дошедшего до него · ${maxDayParam === null ? 'весь срок жизни' : `D0–D${maxDayParam}`}`;
  const horizon = data?.horizon;
  const clampNote = !data?.cohortClamped
    ? null
    : data.cohortWindow
      ? `регистрации обрезаны до ${data.cohortWindow.to}: более поздние ещё не прожили D${horizon}`
      : `в окне нет регистраций, проживших D${horizon}`;

  const emptyMessage =
    data && data.cohortWindow === null
      ? 'В окне нет регистраций, проживших выбранный горизонт дня жизни'
      : isDay
        ? `Нет игроков, живых в D${dayParam}, за выбранную когорту`
        : `Никто из когорты не дошёл до уровня ${levelParam}`;
  // Без горизонта у свежих регистраций меньше времени дойти до уровня: правый край графика занижен.
  const immatureNote =
    !isDay && maxDayParam === null
      ? 'Без «Макс. дня жизни» свежие периоды занижены: их игроки ещё не успели дойти до уровня. Для сравнения периодов задайте горизонт, например 7.'
      : null;

  return (
    <BaseWidget
      {...widgetProps}
      data={hasTrendData(data?.points) ? data : null}
      title="Реклама по уровням: динамика"
      subtitle={[`По периодам регистрации · ${sliceText}`, clampNote].filter(Boolean).join(' · ')}
      emptyMessage={emptyMessage}
      skeletonRows={10}
      toolbar={controls}
    >
      <div className="space-y-6">
        <div>
          <p className="text-sm font-medium mb-2">Реклама на игрока · столбики — {playersName}</p>
          <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
            <ComposedChart data={rows} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis dataKey="label" className="text-xs" />
              <YAxis yAxisId="ads" className="text-xs" tickFormatter={ratioFormat} />
              <YAxis
                yAxisId="players"
                orientation="right"
                className="text-xs"
                tickFormatter={(v) => intFormat.format(v)}
              />
              <Tooltip content={<MainTooltipContent playersName={playersName} />} />
              <Legend />
              <Bar
                yAxisId="players"
                dataKey="players"
                name={playersName}
                fill={PLAYERS_COLOR}
                isAnimationActive={false}
              />
              {AD_LINES.map((l) => (
                <Line
                  key={l.key}
                  yAxisId="ads"
                  type="monotone"
                  dataKey={l.key}
                  name={l.name}
                  stroke={l.color}
                  strokeWidth={l.width}
                  dot={rows.length <= 40}
                  isAnimationActive={false}
                />
              ))}
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {groups.length > 0 && (
          <div>
            <p className="text-sm font-medium mb-2">
              {showShare ? 'Доля игроков, смотревших рекламу в группе плейсментов' : 'Показы на игрока по группам плейсментов'}
            </p>
            <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
              <ComposedChart data={rows} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="label" className="text-xs" />
                <YAxis className="text-xs" tickFormatter={(v) => formatTrendValue(v, valueMode)} />
                <Tooltip
                  order={showShare ? 'value' : 'stack'}
                  formatter={(v) => formatTrendValue(v, valueMode)}
                  labelFormatter={(label) => `Регистрации ${label}`}
                />
                <Legend />
                {groups.map((g) =>
                  showShare ? (
                    <Line
                      key={g.id}
                      type="monotone"
                      dataKey={groupSeriesKey(g.id)}
                      name={g.label}
                      stroke={groupColor(g.id)}
                      dot={false}
                      connectNulls={false}
                      isAnimationActive={false}
                    />
                  ) : (
                    <Bar
                      key={g.id}
                      dataKey={groupSeriesKey(g.id)}
                      name={g.label}
                      stackId="groups"
                      fill={groupColor(g.id)}
                      isAnimationActive={false}
                    />
                  )
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}

        {immatureNote && <p className="text-xs text-amber-600">{immatureNote}</p>}

        <PeriodsTable rows={rows} total={totalRow} playersName={playersName} />

        <p className="text-xs text-muted-foreground">
          Ось — период регистрации{step === 'week' ? ' (неделя с понедельника)' : ''}; * — период попал в окно дат не
          целиком. Показ — сетевая реклама
          без домашней VIP. {isDay
            ? `«Живы в D${dayParam}» — игроки с любым событием в этот день; когорта — все с событиями в D0–D${dayParam}.`
            : `«Дошли до ур. ${levelParam}» — максимальный уровень в горизонте не ниже ${levelParam}; когорта — все с событиями в горизонте.`}{' '}
          Платформа, гео и версии клиента режут события, как в таблице.
        </p>
      </div>
    </BaseWidget>
  );
}

export default AdLevelsTrendWidget;
